import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { isValidEmail } from "../utils/isValidEmail";
import { useAuth } from "../contexts/AuthContext";
import { Alert, Box, Button, CircularProgress, IconButton, MenuItem, Skeleton, TextField, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import TeamCrest from "./ui/TeamCrest";

const JERSEY_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const TEAM_COLUMNS = "id, name, region, division, season, captain_id, official_team_id, sport, age_group, logo_url";

const pill = {
  textTransform: "none",
  fontWeight: 800,
  borderRadius: 999,
  boxShadow: "none",
  whiteSpace: "nowrap",
};
const darkPill = { ...pill, bgcolor: "#111", color: "#fff", "&:hover": { bgcolor: "#000", boxShadow: "none" }, "&.Mui-disabled": { bgcolor: "#d4d4d8", color: "#fff" } };
const lightPill = { ...pill, color: "#111", borderColor: "rgba(0,0,0,0.2)", "&:hover": { borderColor: "#111", bgcolor: "transparent" } };
const field = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } };
const eyebrow = { fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(0,0,0,0.45)" };

const Panel = ({ title, action, children, sx }) => (
  <Box component="section" sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 3, p: { xs: 2, sm: 2.5 }, ...sx }}>
    {(title || action) && (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
        <Typography component="h2" sx={{ fontWeight: 900, fontSize: "1.05rem", color: "#111" }}>
          {title}
        </Typography>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const SizeSelect = ({ value, onChange }) => (
  <TextField select size="small" label="Size" value={value} onChange={(e) => onChange(e.target.value)} sx={field} fullWidth>
    <MenuItem value="">
      <em>Not set</em>
    </MenuItem>
    {JERSEY_SIZES.map((s) => (
      <MenuItem key={s} value={s}>
        {s}
      </MenuItem>
    ))}
  </TextField>
);

/** Name, email, number and size inputs shared by "add" and "edit". */
const PlayerFields = ({ value, onChange }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: typeof e === "string" ? e : e.target.value });
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
      <TextField size="small" label="Player name" value={value.name} onChange={set("name")} sx={{ ...field, gridColumn: "1 / -1" }} />
      <TextField size="small" label="Email" type="email" autoComplete="email" value={value.email} onChange={set("email")} sx={{ ...field, gridColumn: "1 / -1" }} />
      <TextField size="small" label="Jersey #" inputMode="numeric" value={value.jerseyNumber} onChange={set("jerseyNumber")} sx={field} />
      <SizeSelect value={value.jerseySize} onChange={set("jerseySize")} />
    </Box>
  );
};

const EMPTY_PLAYER = { name: "", email: "", jerseyNumber: "", jerseySize: "" };

const playerPayload = (v) => {
  const name = v.name.trim();
  const email = v.email.trim().toLowerCase();
  const jerseyNumber = String(v.jerseyNumber).trim();
  if (!name || !email || !jerseyNumber) throw new Error("Name, email, and jersey number are required.");
  if (!isValidEmail(email)) throw new Error("Enter a valid email address.");
  return { name, email, jersey_number: jerseyNumber, jersey_size: String(v.jerseySize).trim() || null };
};

const RosterEditor = () => {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get("team_id");

  const [team, setTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [teamNameEdit, setTeamNameEdit] = useState("");
  const [teamNameSaving, setTeamNameSaving] = useState(false);
  const [teamNameSaved, setTeamNameSaved] = useState(false);

  const [newPlayer, setNewPlayer] = useState(EMPTY_PLAYER);
  const [adding, setAdding] = useState(false);
  const [addedName, setAddedName] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editPlayer, setEditPlayer] = useState(EMPTY_PLAYER);
  const [editSaving, setEditSaving] = useState(false);

  const isCaptain = useMemo(() => !!team && !!user && team.captain_id === user.id, [team, user]);

  const refreshRoster = useCallback(async () => {
    const { data: rosterRows } = await supabase
      .from("league_team_players")
      .select("id, name, email, jersey_number, jersey_size, user_id")
      .eq("team_id", teamId)
      .order("jersey_number");
    setRoster(rosterRows || []);
  }, [teamId]);

  useEffect(() => {
    if (authLoading || !user || !teamId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data: teamRow } = await supabase.from("league_teams").select(TEAM_COLUMNS).eq("id", teamId).maybeSingle();
        if (cancelled) return;
        if (teamRow && !teamRow.logo_url && teamRow.official_team_id) {
          const { data: official } = await supabase.from("teams").select("logo_url").eq("id", teamRow.official_team_id).maybeSingle();
          if (cancelled) return;
          teamRow.logo_url = official?.logo_url || null;
        }
        setTeam(teamRow || null);
        setTeamNameEdit((teamRow?.name ?? "").trim());
        await refreshRoster();
      } catch (e) {
        if (!cancelled) setError("Could not load roster.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user, teamId, refreshRoster]);

  const saveTeamName = async () => {
    const next = teamNameEdit.trim();
    if (!next || !teamId) return;
    setTeamNameSaving(true);
    setError("");
    try {
      const { data: updated, error: upErr } = await supabase.from("league_teams").update({ name: next }).eq("id", teamId).select(TEAM_COLUMNS).maybeSingle();
      if (upErr) throw upErr;
      if (updated) setTeam((prev) => ({ ...updated, logo_url: updated.logo_url || prev?.logo_url || null }));
      setTeamNameSaved(true);
    } catch (e) {
      setError(e?.message || "Could not update team name.");
    } finally {
      setTeamNameSaving(false);
    }
  };

  const beginEdit = (p) => {
    setEditingId(p.id);
    setEditPlayer({ name: p.name ?? "", email: p.email ?? "", jerseyNumber: String(p.jersey_number ?? ""), jerseySize: p.jersey_size ?? "" });
    setError("");
  };

  const saveEdit = async (p) => {
    setEditSaving(true);
    setError("");
    try {
      const { error: upErr } = await supabase.from("league_team_players").update(playerPayload(editPlayer)).eq("id", p.id).eq("team_id", teamId);
      if (upErr) {
        if (upErr.code === "23505") throw new Error("That email or jersey number is already on this roster. Use unique values.");
        throw upErr;
      }
      await refreshRoster();
      setEditingId(null);
    } catch (e) {
      setError(e?.message || "Could not update player.");
    } finally {
      setEditSaving(false);
    }
  };

  const removePlayer = async (p) => {
    if (!window.confirm(`Remove ${p.name} from the roster?`)) return;
    try {
      await supabase.functions.invoke("delete-roster-player", { body: { team_id: teamId, roster_player_id: p.id } });
      await refreshRoster();
    } catch (e) {
      setError(e?.message || "Could not remove player.");
    }
  };

  const addPlayer = async () => {
    setAdding(true);
    setError("");
    setAddedName("");
    try {
      const base = playerPayload(newPlayer);
      const payload = {
        ...base,
        team_id: teamId,
        sport: String(team?.sport ?? "Soccer").trim() || "Soccer",
        age_group: String(team?.age_group ?? "Adult").trim() || "Adult",
        // If this email belongs to the logged-in user already, auto-link user_id.
        user_id: user?.email?.trim().toLowerCase() === base.email ? user.id : null,
      };
      const { error: insErr } = await supabase.from("league_team_players").insert(payload);
      if (insErr) {
        if (insErr.code === "23505") {
          throw new Error("That email or jersey number is already on this roster. Remove the existing row first or use a different email or number.");
        }
        throw insErr;
      }
      try {
        await supabase.functions.invoke("send-roster-invite", {
          body: { team_id: teamId, player_email: base.email, player_name: base.name, jersey_number: base.jersey_number },
        });
      } catch (_) {
        // Invite email is best-effort; roster insert is the source of truth.
      }
      setNewPlayer(EMPTY_PLAYER);
      setAddedName(base.name);
      await refreshRoster();
    } catch (e) {
      setError(e?.message || "Could not add player.");
    } finally {
      setAdding(false);
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "#000" }} />
      </Box>
    );
  }
  if (!user) return <Navigate to="/register" replace />;
  if (!teamId) {
    return (
      <Box sx={{ py: 6, px: 2, textAlign: "center", maxWidth: 520, mx: "auto" }}>
        <Typography sx={{ color: "#666", mb: 2 }}>Missing team id.</Typography>
        <Button component={Link} to="/" variant="contained" sx={darkPill}>
          Back to My HSL
        </Button>
      </Box>
    );
  }

  const missingSizes = roster.filter((p) => !p.jersey_size).length;
  const missingNumbers = roster.filter((p) => p.jersey_number == null || p.jersey_number === "").length;
  const league = [team?.region, team?.division].filter(Boolean).join(" · ");

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 6, minHeight: "70vh" }}>
      <Button component={Link} to="/" startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 18 }} />} size="small" sx={{ ...pill, color: "#111", px: 1, ml: -1, mb: 1 }}>
        My HSL
      </Button>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>
        {isCaptain ? "Captain tools" : "Roster"}
      </Typography>
      <Typography component="h1" sx={{ fontSize: { xs: "2rem", sm: "2.6rem" }, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.1, color: "#111" }}>
        Manage roster
      </Typography>
      <Typography sx={{ color: "rgba(0,0,0,0.6)", mt: 0.5 }}>
        Players you add get an email invite. Once they sign in with that email they&apos;ll see the team, fixtures and their stats.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mt: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3, mb: 2 }} />
          <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
        </Box>
      ) : (
        <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" }, gap: 2, alignItems: "start" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <Panel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TeamCrest url={team?.logo_url} name={team?.name} size={64} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {league && <Typography sx={eyebrow}>{league}</Typography>}
                  <Typography sx={{ fontSize: { xs: "1.4rem", sm: "1.7rem" }, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.15, color: "#111" }}>
                    {team?.name || "Team"}
                  </Typography>
                </Box>
              </Box>
              {isCaptain ? (
                <Box sx={{ display: "flex", gap: 1, mt: 2, alignItems: "center" }}>
                  <TextField
                    size="small"
                    label="Team name"
                    value={teamNameEdit}
                    onChange={(e) => {
                      setTeamNameEdit(e.target.value);
                      setTeamNameSaved(false);
                    }}
                    fullWidth
                    sx={field}
                  />
                  <Button
                    variant="contained"
                    onClick={saveTeamName}
                    disabled={teamNameSaving || !teamNameEdit.trim() || teamNameEdit.trim() === (team?.name ?? "").trim()}
                    sx={{ ...darkPill, flexShrink: 0, px: 2 }}
                  >
                    {teamNameSaving ? "Saving…" : teamNameSaved ? "Saved" : "Save"}
                  </Button>
                </Box>
              ) : (
                <Typography sx={{ mt: 1.5, fontSize: "0.85rem", color: "#888" }}>Only the captain can change the team name.</Typography>
              )}
            </Panel>

            <Panel
              title="Players"
              action={
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: "rgba(0,0,0,0.5)" }}>
                  {roster.length} on roster
                </Typography>
              }
            >
              {roster.length > 0 && (missingSizes > 0 || missingNumbers > 0 ? (
                <Box sx={{ mb: 1, p: 1.25, borderRadius: 2, bgcolor: "#fff7ed", color: "#9a3412", fontSize: "0.85rem", fontWeight: 700 }}>
                  {[
                    missingNumbers > 0 && `${missingNumbers} without a jersey number`,
                    missingSizes > 0 && `${missingSizes} without a jersey size`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Box>
              ) : (
                <Typography sx={{ mb: 1, fontSize: "0.85rem", color: "#16a34a", fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CheckIcon sx={{ fontSize: 18 }} /> Everyone has a number and size
                </Typography>
              ))}
              {roster.map((p) =>
                editingId === p.id ? (
                  <Box key={p.id} sx={{ my: 1, p: 1.5, borderRadius: 2, bgcolor: "#fafafa", border: "1px solid #ececec" }}>
                    <Typography sx={{ ...eyebrow, mb: 1.25 }}>Editing {p.name}</Typography>
                    <PlayerFields value={editPlayer} onChange={setEditPlayer} />
                    <Box sx={{ display: "flex", gap: 1, mt: 1.5, justifyContent: "flex-end" }}>
                      <Button variant="outlined" size="small" onClick={() => setEditingId(null)} sx={lightPill}>
                        Cancel
                      </Button>
                      <Button variant="contained" size="small" onClick={() => saveEdit(p)} disabled={editSaving} sx={{ ...darkPill, px: 2 }}>
                        {editSaving ? "Saving…" : "Save"}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    key={p.id || p.email}
                    sx={{ display: "grid", gridTemplateColumns: "38px minmax(0,1fr) auto", gap: 1, alignItems: "center", py: 1, borderBottom: "1px solid #f4f4f4", "&:last-of-type": { borderBottom: 0 } }}
                  >
                    <Typography sx={{ color: "#999", fontVariantNumeric: "tabular-nums", fontSize: "0.9rem", fontWeight: 700 }}>
                      {p.jersey_number ?? "–"}
                    </Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 800, fontSize: "0.92rem", color: "#111" }}>
                          {p.name}
                          {p.user_id && p.user_id === user.id ? " (you)" : ""}
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            flexShrink: 0,
                            px: 0.75,
                            borderRadius: 1,
                            fontSize: "0.68rem",
                            fontWeight: 800,
                            bgcolor: p.jersey_size ? "#f1f1f1" : "#fff7ed",
                            color: p.jersey_size ? "#555" : "#c2410c",
                          }}
                        >
                          {p.jersey_size || "No size"}
                        </Box>
                      </Box>
                      <Typography noWrap sx={{ fontSize: "0.76rem", color: "#888" }}>
                        {p.email}
                      </Typography>
                    </Box>
                    {isCaptain && (
                      <Box sx={{ display: "flex", gap: 0.25 }}>
                        <Button size="small" onClick={() => beginEdit(p)} sx={{ ...pill, color: "#111", minWidth: 0, px: 1.25, display: { xs: "none", sm: "inline-flex" } }}>
                          Edit
                        </Button>
                        <Button size="small" onClick={() => removePlayer(p)} sx={{ ...pill, color: "#dc2626", minWidth: 0, px: 1.25, display: { xs: "none", sm: "inline-flex" } }}>
                          Remove
                        </Button>
                        <IconButton size="small" aria-label={`Edit ${p.name}`} onClick={() => beginEdit(p)} sx={{ color: "#111", display: { sm: "none" } }}>
                          <EditOutlinedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                        <IconButton size="small" aria-label={`Remove ${p.name}`} onClick={() => removePlayer(p)} sx={{ color: "#dc2626", display: { sm: "none" } }}>
                          <DeleteOutlineRoundedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                )
              )}
              {roster.length === 0 && <Typography sx={{ color: "#888", fontSize: "0.9rem" }}>No players yet. Add your first one below.</Typography>}
            </Panel>
          </Box>

          <Panel title="Add a player" sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
            {isCaptain ? (
              <>
                <PlayerFields
                  value={newPlayer}
                  onChange={(v) => {
                    setNewPlayer(v);
                    setAddedName("");
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={addPlayer}
                  disabled={adding}
                  startIcon={adding ? <CircularProgress size={14} color="inherit" /> : <PersonAddAlt1RoundedIcon sx={{ fontSize: 18 }} />}
                  sx={{ ...darkPill, mt: 1.5, py: 1 }}
                >
                  {adding ? "Adding…" : "Add player"}
                </Button>
                {addedName ? (
                  <Typography sx={{ mt: 1, fontSize: "0.82rem", color: "#16a34a", fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 16 }} /> {addedName} added and invited by email
                  </Typography>
                ) : (
                  <Typography sx={{ mt: 1, fontSize: "0.78rem", color: "#888" }}>Name, email and number are required. Size is optional.</Typography>
                )}
              </>
            ) : (
              <Typography sx={{ fontSize: "0.9rem", color: "#666" }}>Only the captain can add players to this roster.</Typography>
            )}
          </Panel>
        </Box>
      )}
    </Box>
  );
};

export default RosterEditor;
